/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import {
   HealthcareProviderNfzConfiguration,
   PractitionerNfzConfiguration,
} from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { Translate } from '@dreryk/react-common';
import { FormContent, FormPanel, FormRouteLeavingPrompt, FormValidationSnackbar } from '@dreryk/react-components';

import { StyledFormSectionHeader } from '../../../common/StyledFormSectionHeader';
import { nfzLoginTranslationKeys } from '../../../translations';

import { EwusLoginSaveAction } from './EwusLoginSaveAction';
import { EwusNewPasswordSaveAction } from './EwusNewPasswordSaveAction';
import { EzwmLoginSaveAction } from './EzwmLoginSaveAction';
import { EzwmNewPasswordSaveAction } from './EzwmNewPasswordSaveAction';
import { LoginField } from './fields/LoginField';
import { NewPasswordField } from './fields/NewPasswordField';
import { useNfzLoginFormController } from './NfzLoginForm.hooks';
import { NfzPasswordChangeAction } from './NfzPasswordChangeAction';
import { NfzPasswordChangeCancelAction } from './NfzPasswordChangeCancelAction';
import { PasswordField } from './fields/PasswordField';
import { PersonnelCodeField } from './fields/PersonnelCodeField';
import { RepeatNewPasswordField } from './fields/RepeatNewPasswordField';
import { StyledNfzLoginForm } from './StyledNfzLoginForm';
import { MFA_SECRET_PLACEHOLDER_VALUE, MfaSecretField } from './fields/MfaSecretField';
import {
   contractingPartyTypeVisitor,
   ewusFormPartNotVisible,
   ewusFormPartVisible,
   ewusLoginActionVisible,
   ewusNewPasswordSaveActionVisible,
   ezwmFormPartVisible,
   ezwmLoginActionVisible,
   ezwmMfaFormPartVisible,
   ezwmMfaSecretFormPartVisible,
   ezwmMfaTotpFormPartVisible,
   ezwmNewPasswordSaveActionVisible,
   mfaSecretFormPartVisible,
   mfaTotpFormPartVisible,
   passwordChangeFormPartNotVisible,
   passwordChangeFormPartVisible,
} from './NfzLoginForm.utils';
import { MfaStrategyField } from './fields/MfaStrategyField';
import { PasswordChangeEnabledField } from './fields/PasswordChangeEnabledField';
import { MfaTotpField } from './fields/MfaTotpField';
import { EwusEnabledField } from './fields/EwusEnabledField';
import { EzwmEnabledField } from './fields/EzwmEnabledField';

type NfzLoginFormRendererProps = Readonly<{
   nfzConfiguration: HealthcareProviderNfzConfiguration | null;
   currentPractitionerNfzConfiguration: PractitionerNfzConfiguration | null;
   ezwmEnabled: boolean;
   ewusEnabled: boolean;
}>;

export const NfzLoginFormRenderer = (props: NfzLoginFormRendererProps): JSX.Element => {
   const controller = useNfzLoginFormController({
      currentPractitionerNfzConfiguration: props.currentPractitionerNfzConfiguration,
      ewusEnabled: props.ewusEnabled,
      ezwmEnabled: props.ezwmEnabled,
   });

   const enableChangePassword = useCallback(() => {
      controller.setValue('passwordChangeEnabled', true);
   }, [controller]);

   const resetPassword = useCallback(() => {
      controller.setValue('password', null);
      controller.setValue('passwordChangeEnabled', false);
      if (ewusFormPartVisible(controller)) {
         if (mfaSecretFormPartVisible(controller)) {
            controller.setValue('mfaSecret', MFA_SECRET_PLACEHOLDER_VALUE);
         }

         if (mfaTotpFormPartVisible(controller)) {
            controller.setValue('mfaTotp', null);
         }
      }
      if (ewusFormPartNotVisible(controller) && ezwmFormPartVisible(controller)) {
         if (ezwmMfaSecretFormPartVisible(controller)) {
            controller.setValue('mfaSecret', MFA_SECRET_PLACEHOLDER_VALUE);
         }

         if (ezwmMfaTotpFormPartVisible(controller)) {
            controller.setValue('mfaTotp', null);
         }
      }
   }, [controller]);

   return (
      <>
         <FormPanel>
            <FormContent testId={'nfzIntegration'}>
               <StyledFormSectionHeader>
                  {<Translate>{nfzLoginTranslationKeys.nfzIntegration}</Translate>}
               </StyledFormSectionHeader>
               <StyledNfzLoginForm noValidate>
                  <FormContent testId={'nfzLogin'}>
                     {contractingPartyTypeVisitor({ nfzConfiguration: props.nfzConfiguration })({
                        visitPerson: ({ personnelCodeRequired }) => (
                           <PersonnelCodeField register={controller.register} required={personnelCodeRequired} />
                        ),
                     })}
                     <LoginField register={controller.register} />
                     <PasswordField register={controller.register} />
                     <PasswordChangeEnabledField register={controller.register} />
                     {passwordChangeFormPartVisible(controller) ? (
                        <>
                           <NewPasswordField register={controller.register} />
                           <RepeatNewPasswordField register={controller.register} />
                        </>
                     ) : null}
                     <EwusEnabledField register={controller.register} />
                     {ewusFormPartNotVisible(controller) ? <EzwmEnabledField register={controller.register} /> : null}
                     {ezwmMfaFormPartVisible(controller) ? (
                        <>
                           <MfaStrategyField register={controller.register} />
                           {ezwmMfaSecretFormPartVisible(controller) && (
                              <MfaSecretField
                                 register={controller.register}
                                 setValue={controller.setValue}
                                 watch={controller.watch}
                                 mfaSecretPresent={Boolean(props.currentPractitionerNfzConfiguration?.mfaSecretPresent)}
                              />
                           )}
                           {ezwmMfaTotpFormPartVisible(controller) && <MfaTotpField register={controller.register} />}
                        </>
                     ) : null}
                     {ewusFormPartVisible(controller) ? (
                        <>
                           <MfaStrategyField register={controller.register} />
                           {mfaSecretFormPartVisible(controller) && (
                              <MfaSecretField
                                 register={controller.register}
                                 setValue={controller.setValue}
                                 watch={controller.watch}
                                 mfaSecretPresent={Boolean(props.currentPractitionerNfzConfiguration?.mfaSecretPresent)}
                              />
                           )}
                           {mfaTotpFormPartVisible(controller) && <MfaTotpField register={controller.register} />}
                        </>
                     ) : null}
                  </FormContent>
               </StyledNfzLoginForm>
            </FormContent>
         </FormPanel>

         {passwordChangeFormPartVisible(controller) ? <NfzPasswordChangeCancelAction onClick={resetPassword} /> : null}
         {passwordChangeFormPartNotVisible(controller) ? (
            <NfzPasswordChangeAction onClick={enableChangePassword} />
         ) : null}

         {ewusNewPasswordSaveActionVisible(controller) ? (
            <EwusNewPasswordSaveAction handleSubmit={controller.handleSubmit} onSuccess={resetPassword} />
         ) : null}
         {ewusLoginActionVisible(controller) ? (
            <EwusLoginSaveAction handleSubmit={controller.handleSubmit} onSuccess={resetPassword} />
         ) : null}

         {ezwmNewPasswordSaveActionVisible(controller) ? (
            <EzwmNewPasswordSaveAction handleSubmit={controller.handleSubmit} onSuccess={resetPassword} />
         ) : null}

         {ezwmLoginActionVisible(controller) ? (
            <EzwmLoginSaveAction handleSubmit={controller.handleSubmit} onSuccess={resetPassword} />
         ) : null}

         <FormRouteLeavingPrompt formState={controller.formState} />
         <FormValidationSnackbar formState={controller.formState} />
      </>
   );
};

type NfzLoginFormProps = {
   readonly currentHealthcareProviderNfzConfiguration: HealthcareProviderNfzConfiguration | null;
   readonly currentPractitionerNfzConfiguration: PractitionerNfzConfiguration | null;
   readonly currentHealthcareProviderEwusEnabled: boolean;
   readonly currentHealthcareProviderEzwmEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const NfzLoginForm = (props: NfzLoginFormProps): JSX.Element => {
   return (
      <NfzLoginFormRenderer
         nfzConfiguration={props.currentHealthcareProviderNfzConfiguration}
         currentPractitionerNfzConfiguration={props.currentPractitionerNfzConfiguration}
         ewusEnabled={props.currentHealthcareProviderEwusEnabled}
         ezwmEnabled={props.currentHealthcareProviderEzwmEnabled}
      />
   );
};
